import React, { useState, useEffect } from 'react';

interface SerialValidationResult {
  whid: string;
  organizationId: string;
  inventoryItemId: string;
  serialNumber: string;
  itemNumber: string;
  description: string;
  assetNumber: string;
  serialStatus: string;
  snRepairEligible: string;
}

interface SerialSelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedResult: SerialValidationResult) => void;
  results: SerialValidationResult[];
  isDarkTheme?: boolean;
}

const SerialSelectionPopup: React.FC<SerialSelectionPopupProps> = ({
  isOpen,
  onClose,
  onSelect,
  results,
  isDarkTheme = false
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Clear selection when popup opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(null);
      // Center the popup when it opens
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, handleMouseMove]);

  if (!isOpen) return null;

  const handleOk = () => {
    if (selectedIndex !== null && results[selectedIndex]) {
      onSelect(results[selectedIndex]);
      onClose();
    }
  };

  const columns = [
    { key: 'snRepairEligible', label: 'Repair Eligible' },
    { key: 'whid', label: 'WHID' },
    { key: 'serialNumber', label: 'Serial Number' },
    { key: 'itemNumber', label: 'Item Number' },
    { key: 'description', label: 'Description' },
    { key: 'assetNumber', label: 'Asset Number' },
    { key: 'serialStatus', label: 'Serial Status' }
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: isDarkTheme ? '#2a2a2a' : '#fff',
        border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
        borderRadius: '8px',
        padding: '20px',
        maxWidth: '90%',
        maxHeight: '80%',
        overflow: 'auto',
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}>
        <h3 style={{ 
          margin: '0 0 15px 0',
          color: isDarkTheme ? '#fff' : '#000',
          cursor: 'grab',
          userSelect: 'none'
        }}
        onMouseDown={handleMouseDown}
        >
          Select Serial Number
        </h3>
        
        <div style={{ marginBottom: '15px', maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: isDarkTheme ? '#444' : '#f5f5f5' }}>
                <th style={{ 
                  padding: '8px', 
                  border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
                  color: isDarkTheme ? '#fff' : '#000',
                  fontSize: '12px'
                }}>
                  Select
                </th>
                {columns.map(col => (
                  <th key={col.key} style={{ 
                    padding: '8px', 
                    border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
                    color: isDarkTheme ? '#fff' : '#000',
                    fontSize: '12px'
                  }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => (
                <tr key={index} style={{
                  backgroundColor: selectedIndex === index 
                    ? (isDarkTheme ? '#0d47a1' : '#e3f2fd')
                    : (isDarkTheme ? '#2a2a2a' : '#fff')
                }}>
                  <td style={{ 
                    padding: '8px', 
                    border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
                    textAlign: 'center'
                  }}>
                    <input
                      type="radio"
                      name="serialSelection"
                      checked={selectedIndex === index}
                      onChange={() => setSelectedIndex(index)}
                    />
                  </td>
                  {columns.map(col => (
                    <td key={col.key} style={{ 
                      padding: '8px', 
                      border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
                      color: isDarkTheme ? '#fff' : '#000',
                      fontSize: '12px'
                    }}>
                      {result[col.key as keyof SerialValidationResult] || ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleOk}
            disabled={selectedIndex === null}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedIndex !== null ? '#007bff' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: selectedIndex !== null ? 'pointer' : 'not-allowed'
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default SerialSelectionPopup;